import * as React from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';

import { Loader2 } from '@/components/ui/icon';
import { DURATIONS, EASINGS } from '@/lib/animation-constants';
import { hapticSelection } from '@/lib/haptics';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'btn-premium inline-flex items-center justify-center gap-2 whitespace-nowrap text-body font-semibold ring-offset-background cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation',
  {
    variants: {
      variant: {
        default: 'btn-premium-default text-primary-foreground',
        destructive: 'btn-premium-destructive text-destructive-foreground',
        outline: 'btn-premium-outline text-foreground',
        secondary: 'btn-premium-secondary text-foreground',
        ghost:
          'btn-premium-ghost border border-transparent bg-transparent text-muted-foreground hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2.5',
        sm: 'btn-premium-sm min-h-11 px-3.5 py-2 text-meta sm:text-body',
        lg: 'btn-premium-lg h-12 px-8',
        icon: 'btn-premium-icon size-11 min-h-11 min-w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /** @deprecated Prefer Base UI's render prop for new call sites. */
    asChild?: boolean;
    loading?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    loading = false,
    disabled,
    onClick,
    children,
    render,
    nativeButton,
    style,
    ...props
  },
  ref,
) {
  const legacyRender =
    asChild && React.isValidElement(children) ? (children as React.ReactElement) : render;

  return (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      render={legacyRender}
      nativeButton={asChild ? false : nativeButton}
      disabled={loading || disabled}
      aria-busy={loading || undefined}
      onClick={(event) => {
        if (!disabled && !loading) {
          hapticSelection();
          onClick?.(event);
        }
      }}
      style={{
        transitionDuration: `${DURATIONS.fast}ms`,
        transitionTimingFunction: EASINGS.decelerate,
        ...(typeof style === 'object' ? style : {}),
      }}
      {...props}
    >
      {asChild ? undefined : loading ? (
        <>
          <Loader2 data-icon="inline-start" className="animate-spin" />
          {children}
        </>
      ) : (
        children
      )}
    </ButtonPrimitive>
  );
});

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
